import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { AiControlledCommand } from 'aiControlledCommands/types';
import { VIEWPOINT_DOMAIN } from '../domain';
import { AI_VIEW_IDS, type AiViewId } from '../aiViewIds';
import { CHANGE_VIEW_CONTEXT_DESCRIPTION, WHEN_TO_USE } from './descriptions';
import typia from 'typia';
import { staticRejection, toCommandRejection, } from 'aiControlledCommands/validationRejection';
import { CommandValidationResult, defineAiControlledCommand, } from 'aiControlledCommands/defineAiControlledCommand';
import { ViewIds } from '@ardoq/api-types';
import { ViewpointModeViewKey } from 'views/environmentals/viewSettings/types';
import { isRecord } from '@ardoq/common-helpers';
import { isViewHiddenByBlockDiagramSwap, toRenderedViewpointViewId, } from 'views/environmentals/getViewpointModeSupportedViews';
import { VIEWPOINT_MODE_VIEW_DEFINITIONS, viewIdToUserFriendlyName, } from 'views/metaInfoConsts';
import { dispatchAction } from '@ardoq/rxbeach';
import { setActiveMainTabLeft } from 'streams/views/mainContent/actions';
// typia compiles the JSDoc here into the command's JSON schema, so these
// comments are LLM-facing — keep them accurate.
export type ChangeViewToPayload = {
    /**
     * The view the user asked for, in their own words, verbatim. Write this
     * before choosing 'view'.
     */
    requestedView: string;
    /**
     * The view to open. Choose it only when the user's words match its display
     * name in 'viewNames' exactly; otherwise use 'unsupported'. Never pick a
     * similar-sounding view instead.
     */
    view: AiViewId;
};
export type ChangeViewUnsupportedPayload = {
    /**
     * Use this when the user asks to open or switch to a view or chart type
     * that has no match in 'viewNames' — including a view or chart type
     * Ardoq does not have. Do not substitute
     * the closest-sounding view from 'viewNames' instead.
     */
    view: 'unsupported';
    /** The view or chart type the user asked for, verbatim, for logging only. */
    requestedView: string;
};
export type ChangeViewPayload = ChangeViewToPayload | ChangeViewUnsupportedPayload;
type ChangeViewContext = {
    currentView: ViewIds | null;
    /** Maps each switchable view id to its human-readable display name */
    viewNames: Partial<Record<ViewpointModeViewKey, string>>;
    /** Display names of views that exist in Ardoq but cannot be opened now */
    unavailableViews: string[];
};
const getAiViewNames = (switchableViewIds: ViewIds[]): Partial<Record<ViewpointModeViewKey, string>> => Object.fromEntries(AI_VIEW_IDS.filter(viewId => switchableViewIds.includes(viewId) &&
    !isViewHiddenByBlockDiagramSwap(viewId)).map(viewId => [viewId, viewIdToUserFriendlyName[viewId]]));
const getUnavailableViewNames = (viewNames: Partial<Record<ViewpointModeViewKey, string>>): string[] => {
    const availableNames = new Set(Object.values(viewNames));
    const unavailableNames = VIEWPOINT_MODE_VIEW_DEFINITIONS.map(({ id }) => id)
        .filter(viewId => !(viewId in viewNames))
        .filter(viewId => !isViewHiddenByBlockDiagramSwap(viewId))
        .map(viewId => viewIdToUserFriendlyName[viewId])
        .filter(name => !availableNames.has(name));
    return [...new Set(unavailableNames)];
};
export type BuildChangeViewCommandArgs = {
    currentView: ViewIds | null;
    switchableViewIds: ViewIds[];
};
export const buildChangeViewCommand = ({ currentView, switchableViewIds, }: BuildChangeViewCommandArgs): AiControlledCommand<typeof changeViewCommandDefinition> => {
    const viewNames = getAiViewNames(switchableViewIds);
    return {
        ...changeViewCommandDefinition,
        context: {
            currentView,
            viewNames,
            unavailableViews: getUnavailableViewNames(viewNames),
        },
    };
};
export const executeChangeView = (payload: ChangeViewPayload): void => {
    if (payload.view === 'unsupported')
        return;
    dispatchAction(setActiveMainTabLeft({
        activeTabId: toRenderedViewpointViewId(payload.view),
        isViewpointMode: true,
    }));
};
const isChangeViewContext = (context: unknown): context is ChangeViewContext => isRecord(context) &&
    'currentView' in context &&
    isRecord(context.viewNames) &&
    Array.isArray(context.unavailableViews);
const normalizeViewText = (text: string): string => text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
const textsOverlap = (first: string, second: string): boolean => first.includes(second) || second.includes(first);
const matchesViewName = (requestedView: string, viewName: string): boolean => {
    const requested = normalizeViewText(requestedView);
    const name = normalizeViewText(viewName);
    return requested !== '' && name !== '' && textsOverlap(requested, name);
};
const findConfusedViewName = (requestedView: string, chosenViewName: string, knownViewNames: string[]): string | undefined => {
    if (matchesViewName(requestedView, chosenViewName))
        return undefined;
    return knownViewNames
        .filter(name => name !== chosenViewName)
        .find(name => matchesViewName(requestedView, name));
};
type ConfusedViewReasonArgs = {
    requestedView: string;
    confusedViewName: string;
    chosenViewName: string;
};
const buildConfusedViewReason = ({ requestedView, confusedViewName, chosenViewName, }: ConfusedViewReasonArgs): string => `The user asked for "${requestedView}", which is "${confusedViewName}", not "${chosenViewName}". Open ${confusedViewName} if it is in 'viewNames'; otherwise respond with view: 'unsupported'.`;
const validateChangeView = (rawPayload: unknown, { viewNames, unavailableViews }: ChangeViewContext): CommandValidationResult<ChangeViewPayload> => {
    if (!(() => { const _io0 = (input: any): boolean => "string" === typeof input.requestedView && ("bubble" === input.view || "timeline" === input.view || "componentMatrix2" === input.view || "dependencyMap" === input.view || "relationships3" === input.view || "blocks" === input.view || "flexibleBlockDiagram" === input.view || "modernizedBlockDiagram" === input.view); const _io1 = (input: any): boolean => "unsupported" === input.view && "string" === typeof input.requestedView; const _iu0 = (input: any): any => (() => {
        if ("unsupported" === input.view)
            return _io1(input);
        else if ("bubble" === input.view || "timeline" === input.view || "componentMatrix2" === input.view || "dependencyMap" === input.view || "relationships3" === input.view || "blocks" === input.view || "flexibleBlockDiagram" === input.view || "modernizedBlockDiagram" === input.view)
            return _io0(input);
        else
            return false;
    })(); return (input: any): input is ChangeViewPayload => "object" === typeof input && null !== input && _iu0(input); })()(rawPayload)) {
        return {
            status: 'rejected',
            reason: 'The AI returned an invalid view to open.',
        };
    }
    if (rawPayload.view !== 'unsupported' && !(rawPayload.view in viewNames)) {
        return toCommandRejection(staticRejection('unavailableView', 'The AI asked to open a view that is not available.'));
    }
    if (rawPayload.view === 'unsupported') {
        return { status: 'valid', payload: rawPayload };
    }
    const chosenViewName = viewNames[rawPayload.view] ?? '';
    const confusedViewName = findConfusedViewName(rawPayload.requestedView, chosenViewName, [...Object.values(viewNames), ...unavailableViews]);
    if (confusedViewName !== undefined) {
        return toCommandRejection({
            kind: 'confusedViewName',
            buildReason: showName => buildConfusedViewReason({
                requestedView: showName(rawPayload.requestedView),
                confusedViewName,
                chosenViewName,
            }),
        });
    }
    return { status: 'valid', payload: rawPayload };
};
const isChangeViewApplied = (payload: ChangeViewPayload, { currentView }: ChangeViewContext): boolean => payload.view === 'unsupported' ||
    toRenderedViewpointViewId(payload.view) === currentView;
export const changeViewCommandDefinition = defineAiControlledCommand<ChangeViewPayload, ChangeViewContext>({
    name: AiControlledCommandId.CHANGE_VIEW,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ChangeViewPayload: {
                    oneOf: [
                        {
                            $ref: "#/components/schemas/ChangeViewToPayload"
                        },
                        {
                            $ref: "#/components/schemas/ChangeViewUnsupportedPayload"
                        }
                    ]
                },
                ChangeViewToPayload: {
                    type: "object",
                    properties: {
                        requestedView: {
                            type: "string",
                            description: "The view the user asked for, in their own words, verbatim. Write this\nbefore choosing 'view'."
                        },
                        view: {
                            oneOf: [
                                {
                                    "const": "bubble"
                                },
                                {
                                    "const": "timeline"
                                },
                                {
                                    "const": "componentMatrix2"
                                },
                                {
                                    "const": "dependencyMap"
                                },
                                {
                                    "const": "relationships3"
                                },
                                {
                                    "const": "blocks"
                                },
                                {
                                    "const": "flexibleBlockDiagram"
                                },
                                {
                                    "const": "modernizedBlockDiagram"
                                }
                            ],
                            description: "The view to open. Choose it only when the user's words match its display\nname in 'viewNames' exactly; otherwise use 'unsupported'. Never pick a\nsimilar-sounding view instead."
                        }
                    },
                    required: [
                        "requestedView",
                        "view"
                    ]
                },
                ChangeViewUnsupportedPayload: {
                    type: "object",
                    properties: {
                        view: {
                            "const": "unsupported",
                            description: "Use this when the user asks to open or switch to a view or chart type\nthat has no match in 'viewNames' \u2014 including a view or chart type\nArdoq does not have. Do not substitute\nthe closest-sounding view from 'viewNames' instead."
                        },
                        requestedView: {
                            type: "string",
                            description: "The view or chart type the user asked for, verbatim, for logging only."
                        }
                    },
                    required: [
                        "view",
                        "requestedView"
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ChangeViewPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: CHANGE_VIEW_CONTEXT_DESCRIPTION,
    isContext: isChangeViewContext,
    validate: validateChangeView,
    execute: executeChangeView,
    isApplied: isChangeViewApplied,
});
